import React, { useState } from 'react';
import { Text, Icon, TooltipHost, ITooltipHostStyles, IconButton, ActionButton, IIconStyles, getTheme, BaseButton } from '@fluentui/react';
import { getBwjPageHeaderStyles } from './styles';

export enum NavMenuSlotType {
  Fixed,
  Collapsable,
}

type OnPageNavMenuToggleHandler = (menuName: string, isCollasped: boolean) => void;

interface INavMenuSlotProps {
  name: string;
  slotType: NavMenuSlotType;
  onToggle?: OnPageNavMenuToggleHandler;
}

const NavMenuSlot: React.FunctionComponent<INavMenuSlotProps> = (props) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const menuToggleHostStyles: Partial<ITooltipHostStyles> = { root: { display: 'inline-block' } };

  const styles = getBwjPageHeaderStyles();
  const theme = getTheme();
  const collapseButtonStyles: IIconStyles = {
    root: {
      fontSize: theme.fonts.smallPlus.fontSize,
      color: theme.palette.neutralPrimary,
    }
  };
  const tooltipText = isCollapsed ? 'expand menu' : 'collapse menu';
  const iconName = isCollapsed ? 'DoubleChevronRight12' : 'DoubleChevronLeft12';
  const slotClass = isCollapsed ? styles.collaspedMenuSlot : styles.menuSlot;

  const onCollapseButtonClick = () => {
    const ic = !isCollapsed;
    !!props.onToggle && props.onToggle(props.name, ic);
    setIsCollapsed(ic);
  }

  return (
    <div className={slotClass}>
      <TooltipHost id={props.name}
        content={tooltipText}
        calloutProps={{ gapSpace: 0}}
        styles={menuToggleHostStyles}
      >
        <IconButton iconProps={{ iconName, styles: collapseButtonStyles}}
          title={tooltipText}
          ariaLabel={tooltipText}
          onClick={onCollapseButtonClick}
        />
      </TooltipHost>
    </div>
  );
};

export interface INavMenuSlotInfo extends INavMenuSlotProps {}

export interface IActionButtonInfo {
  id: string;
  name: string;
  iconName: string;
  disabled?: boolean;
  onClick: React.MouseEventHandler<HTMLDivElement | HTMLAnchorElement | HTMLButtonElement | BaseButton | HTMLSpanElement>;
}

export interface IBwjPageHeaderProps {
  title: string;
  subtitle?: string | undefined;
  titleIconName?: string;
  navMenuSlots?: INavMenuSlotInfo[];
  actionButtons?: IActionButtonInfo[];
}

export const BwjPageHeader: React.FunctionComponent<IBwjPageHeaderProps> = (props) => {

  const styles = getBwjPageHeaderStyles();
  const titleFontSize = getTheme().fonts.xxLarge.fontSize;
  const headerIconStyles: IIconStyles = {
    root: {
      fontSize: titleFontSize,
      width: titleFontSize,
      height: titleFontSize,
    }
  };

  return (
    <header className={styles.header}>
      <h1 className={styles.title}>
        {!!props.titleIconName ?
        <span>
          <Icon iconName={props.titleIconName} styles={headerIconStyles} />&nbsp;
        </span> : null}
        <Text variant={'xxLarge'} nowrap>{props.title}</Text>
      </h1>
      {!!props.subtitle &&
      <h2 className={styles.subtitle}>{props.subtitle}</h2>}
      <div className={styles.buttonRow}>
        <div className={styles.menuToggleTray}>
          {props.navMenuSlots?.map(menu => <NavMenuSlot key={menu.name} {...menu} />)}
        </div>
        <div className={styles.actionButtonTray}>
          {props.actionButtons?.map(button =>
          <ActionButton
            key={button.id}
            iconProps={{ iconName: button.iconName}}
            onClick={button.onClick}
            disabled={button.disabled}
          >
            {button.name}
          </ActionButton>
          )}
        </div>
      </div>
    </header>
  );
};
